/**
 * @module employee_attributes
 *
 * Used by companies as the attributes/fields collected for each of its employees.
 */
import { createReducer, current } from '@reduxjs/toolkit';
import { userActions } from '~/behaviors/actions/user.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { employeeAttributeActions } from '~/behaviors/actions/employee_attribute.actions';
import { accountActions } from '~/behaviors/actions/account.actions';
import { benchmarkActions } from '~/behaviors/actions/benchmark.actions';
import { surveyReportingActions } from '~/behaviors/actions/survey_reporting.actions';
import { EmployeeAttributes } from '~/interfaces/employee.interface';
import { handleUnsafeOptionalChaining } from '~/tools/lint/handleUnsafeOptionalChaining';

const defaultState = {};

const reset = () => defaultState;

const removeEmployeeAttribute = (state, action) => {
  const id = action?.payload?.id;

  if (id) {
    const { [id]: deletedAttr, ...remainingAttrs } = state;

    return remainingAttrs;
  }
};

const addEmployeeAttribute = (state, action) => {
  const employeeAttribute = action?.payload?.employeeAttribute;

  state[employeeAttribute.id] = employeeAttribute;
};

const mergeEmployeeAttributes = (state, action) => {
  const employeeAttributes: EmployeeAttributes | EmployeeAttributes[number][] =
    action?.payload?.entities?.employeeAttributes || {};
  // Some payloads list the attributes, and others key them, as a survey's own attributes are, to keep their ids apart.
  const keyedAttributes: [string | number, EmployeeAttributes[number]][] = Array.isArray(employeeAttributes)
    ? employeeAttributes.map((attr) => [attr.id, attr])
    : Object.entries(employeeAttributes);

  keyedAttributes.forEach(([key, attr]) => {
    state[key] = {
      ...state[key],
      ...attr,
    };
  });
};

const changeDefaultName = (state, action) => {
  const { lang } = handleUnsafeOptionalChaining(action?.payload);

  if (lang) {
    const currentState = current(state);

    Object.keys(currentState).forEach((id) => {
      const name = currentState[id].translations[lang]?.name || state.name;

      state[id] = {
        ...state[id],
        name,
      };
    });
  }
};

const employeeAttributes = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(accountActions.logout.pending, reset)
    .addCase(accountActions.switchCompany.pending, reset)
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.fulfilled, mergeEmployeeAttributes)
    .addCase(employeeAttributeActions.fetchEmployeeAttributeValues.fulfilled, mergeEmployeeAttributes)
    .addCase(surveyReportingActions.fetchReportingResults.fulfilled, mergeEmployeeAttributes)
    .addCase(surveyReportingActions.fetchReportingAttributeResult.fulfilled, mergeEmployeeAttributes)
    .addCase(employeeAttributeActions.modifyEmployeeAttribute.fulfilled, mergeEmployeeAttributes)
    .addCase(employeeAttributeActions.createEmployeeAttribute.fulfilled, addEmployeeAttribute)
    .addCase(employeeAttributeActions.removeEmployeeAttribute.fulfilled, removeEmployeeAttribute)
    .addCase(benchmarkActions.fetchCompanyEmployeeAttributes.fulfilled, mergeEmployeeAttributes)
    .addCase(userActions.changeProfile.fulfilled, changeDefaultName);
});

export default employeeAttributes;
